import { useMutation } from '@tanstack/react-query';
import { errorHandler } from '@app/utils/errorHandler';
import { api } from '@app/utils/api';
import { PANEl_BASE_URL } from '@app/contstants/endpoints';
import { ISelectOption } from '@widgets/multi-select';
import { ERR_BAD_REQUEST } from '@app/contstants/http-codes';
import { ServerError } from '@app/error/ServerError';
import { ACCOUNT_TYPE } from '@entities/account/IAccount';

interface IEditAccountValues {
  accountType: ISelectOption;
  account: string;
  managerId: ISelectOption;
  regionId: ISelectOption;
  clientId: ISelectOption;
  discount?: number | null;
  is_processed?: boolean;
  agents?: { account: ISelectOption | null; cashback: number }[] | null;
}

export const fetch = async (data: IEditAccountValues): Promise<void> => {
  try {
    if (data.accountType.value === ACCOUNT_TYPE.AGENT) {
      await api.post<void>(
        `${PANEl_BASE_URL}/accounts/accounts/${data.account}/create_agent_account/`,
        {
          account: data.account,
          managers: [data.managerId.value],
          region: data.regionId.value,
          client_id: data.clientId?.value,
        }
      );
      return;
    }

    if (data.accountType.value === ACCOUNT_TYPE.REFERRAL) {
      await api.post<void>(
        `${PANEl_BASE_URL}/accounts/accounts/${data.account}/create_referral_account/`,
        {
          managers: [data.managerId.value],
          region: data.regionId.value,
          client_id: data.clientId?.value,
          discount: data.discount,
          is_processed: data.is_processed,
          agents: data.agents?.map(item => ({
            account: item.account?.value,
            cashback: item.cashback,
          })),
        }
      );
    }

    if (data.accountType.value === ACCOUNT_TYPE.MINING) {
      await api.post<void>(
        `${PANEl_BASE_URL}/accounts/accounts/${data.account}/create_mining_account/`,
        {
          managers: [data.managerId.value],
          region: data.regionId.value,
          client_id: data.clientId?.value,
          discount: data.discount,
          is_processed: data.is_processed,
        }
      );
    }
  } catch (e: any) {
    if (e.status === ERR_BAD_REQUEST) {
      throw new ServerError(e.response.data.error);
    }
  }
};

export const useEditAccount = () => {
  const { mutateAsync, isError, isPending, isSuccess, error } = useMutation({
    mutationFn: fetch,
    onError: error => errorHandler(error),
  });

  return {
    fetch: mutateAsync,
    isError,
    isPending,
    isSuccess,
    error,
  };
};
